import { getSupabaseClient } from "../../providers/supabase/supabase";
import type {
  EnchargeInstance,
  EnchargeListResponse,
  EnchargeLookupMatch,
  EnchargePersonInput,
  EnchargeTag,
} from "./types";

async function readFunctionErrorMessage(error: unknown): Promise<string> {
  const fallback =
    error instanceof Error ? error.message : "Failed to call Encharge";
  const context = (error as { context?: Response })?.context;
  if (context && typeof context.json === "function") {
    try {
      const payload = await context.json();
      if (payload && typeof payload === "object" && "message" in payload) {
        return String(payload.message);
      }
    } catch {
      // ignore
    }
  }
  return fallback;
}

async function invokeEncharge<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await getSupabaseClient().functions.invoke(
    "encharge",
    { body },
  );

  if (error) {
    throw new Error(await readFunctionErrorMessage(error));
  }

  if (
    data &&
    typeof data === "object" &&
    "message" in data &&
    "status" in data &&
    typeof data.status === "number" &&
    data.status >= 400
  ) {
    throw new Error(String(data.message));
  }

  return data as T;
}

export async function fetchEnchargePeople(
  instance: EnchargeInstance,
  options: { page?: number; search?: string; category?: string } = {},
): Promise<EnchargeListResponse> {
  return invokeEncharge<EnchargeListResponse>({
    action: "list",
    instance,
    page: options.page ?? 1,
    search: options.search ?? "",
    category: options.category ?? "",
  });
}

export async function upsertEnchargePerson(
  instance: EnchargeInstance,
  person: EnchargePersonInput,
): Promise<unknown> {
  return invokeEncharge({
    action: "upsert",
    instance,
    person,
  });
}

export async function lookupEnchargeSubscriptions(options: {
  emails?: string[];
  companyName?: string;
}): Promise<EnchargeLookupMatch[]> {
  const data = await invokeEncharge<{ matches: EnchargeLookupMatch[] }>({
    action: "lookup",
    emails: options.emails ?? [],
    companyName: options.companyName ?? "",
  });
  return data.matches ?? [];
}

export async function fetchEnchargeTags(
  instance: EnchargeInstance,
): Promise<EnchargeTag[]> {
  const data = await invokeEncharge<{ tags: EnchargeTag[] }>({
    action: "listTags",
    instance,
  });
  return data.tags ?? [];
}

export async function createEnchargeTag(
  instance: EnchargeInstance,
  options: { tag: string; color?: string; description?: string },
): Promise<EnchargeTag[]> {
  const data = await invokeEncharge<{ tags: EnchargeTag[] }>({
    action: "createTag",
    instance,
    tag: options.tag,
    color: options.color,
    description: options.description,
  });
  return data.tags ?? [{ tag: options.tag, color: options.color }];
}

export async function addEnchargeTags(
  instance: EnchargeInstance,
  options: { tags: string[]; email?: string; personId?: string },
): Promise<void> {
  await invokeEncharge({
    action: "addTags",
    instance,
    tags: options.tags,
    email: options.email,
    personId: options.personId,
  });
}

export async function removeEnchargeTags(
  instance: EnchargeInstance,
  options: { tags: string[]; email?: string; personId?: string },
): Promise<void> {
  await invokeEncharge({
    action: "removeTags",
    instance,
    tags: options.tags,
    email: options.email,
    personId: options.personId,
  });
}

export function extractEmailsFromContact(
  emailJsonb: Array<{ email?: string | null }> | null | undefined,
): string[] {
  if (!emailJsonb?.length) return [];
  return [
    ...new Set(
      emailJsonb
        .map((entry) => entry.email?.trim().toLowerCase())
        .filter((email): email is string => !!email),
    ),
  ];
}
